import { useAppSelector } from '@dealstack/frontend/app/hooks/redux.ts';
import { useBulkReleaseMutation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useBulkReleaseMutation.ts';
import { useBulkRevokeMutation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useBulkRevokeMutation.ts';
import { useInvestmentsBulkAction } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useInvestmentsBulkAction.ts';
import { useInvestmentsTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useInvestmentsTable.ts';
import { useInvestorsForInviteModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useInvestorsForInviteModal.ts';
import { useInviteInvestmentWorkflowMutation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useInviteInvestmentWorkflowMutation.ts';
import { useReleaseInvestmentWorkflowMutation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useReleaseInvestmentWorkflowMutation.ts';
import { useInvalidateInvestmentsList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useInvalidateInvestmentsList.ts';
import { ProgressBar } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/ProgressBar/ProgressBar.tsx';
import { useRevokeInvestmentWorkflowMutation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useRevokeInvestmentWorkflowMutation.ts';
import { createBulkActions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/createBulkActions.ts';
import { TransactionTypeBulkDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/TransactionTypeBulkDrawer/TransactionTypeBulkDrawer.tsx';
import { mapInvestmentsTableFilterToDto } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investments-filter-map.ts';
import { InvestmentsBulkSelection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/types.ts';
import {
	getBulkOperationParams,
	getOperationType,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils.ts';
import { bulkUpdateExpectedTransactionType } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import type { Table } from '@tanstack/react-table';
import { EligibilityFilter } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investment.types.ts';
import { SendInvitationModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendInvitationModal/SendInvitationModal.tsx';
import {
	prepareOneSchemaShareAssociationsCache,
	upsertShareAssociations,
} from '@dealstack/frontend/app/services/ShareAssociations.service.ts';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { OneSchemaImporterModal } from '@dealstack/frontend/shared/components/OneSchema/OneSchemaImporterModal.tsx';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { ResourceType } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { OneSchemaTemplateKey } from '@dealstack/shared/lib/frontend/common-ui/oneSchemaTemplateKey.ts';
import {
	BulkUpdateInvestorsVestingDto,
	GetInvestmentsForMipItem,
	GetInvestmentsInvestorInfo,
	ExpectedTransactionType,
	InvestorBulkActionDto,
	OneSchemaShareAssociationRowValueDto,
	PermissionRole,
	SendInvestorInviteDto,
} from '@dealstack/shared/lib/generated';
import { useCallback, useMemo, useRef, useState } from 'react';
import { QuickFilterOption } from '@dealstack/frontend/shared/components/Table/MantineTable/QuickFiltersSelect/QuickFiltersSelect.types';
import { InvestmentWorkflowInvestorDataDrawer } from './InvestmentWorkflowInvestorDataDrawer/InvestmentWorkflowInvestorDataDrawer.tsx';
import { InvestmentWorkflowKycSettingsDrawer } from './InvestmentWorkflowKycSettingsDrawer/InvestmentWorkflowKycSettingsDrawer.tsx';
import { TaxAndComplianceDrawer } from './TaxAndComplianceDrawer/TaxAndComplianceDrawer.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useLocation, useNavigate } from 'react-router-dom';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { VestingTermsBulkDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/VestingTermsBulkDrawer';
import {
	bulkUpdateInvestorVestingTerms,
	getMIPInvestorSecuritiesList,
} from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';

interface OneSchemaErrorRecord<T> {
	data: T;
	errors: Record<string, number[]>;
}

export const InvestmentsTable = () => {
	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();
	const { mipId } = useRequiredParams('mipId');
	const { dealId } = useRequiredParams('dealId');
	const navigate = useNavigate();
	const { search } = useLocation();
	const oneSchemaUserConfig = useAppSelector((state) => state.user.data?.oneSchemaConfig);

	const [eligibilityFilter, setEligibilityFilter] = useState<EligibilityFilter | null>(null);
	const [selectedKycWorkflowId, setSelectedKycWorkflowId] = useState<string | undefined>(
		undefined
	);
	const [kycDrawerOpened, setKycDrawerOpened] = useState(false);
	const [investorDataDrawerOpened, setInvestorDataDrawerOpened] = useState(false);
	const [taxComplianceDrawerOpened, setTaxComplianceDrawerOpened] = useState(false);

	const [inviteModalOpened, setInviteModalOpened] = useState(false);
	const [revokeDrawerOpened, setRevokeDrawerOpened] = useState(false);
	const [bulkRevokeDrawerOpened, setBulkRevokeDrawerOpened] = useState(false);
	const [inviteBulkPayload, setInviteBulkPayload] = useState<InvestorBulkActionDto>({
		selectedIds: [],
	});
	const [selectedInvestmentWorkflowIdForRevoke, setSelectedInvestmentWorkflowIdForRevoke] =
		useState<string | undefined>(undefined);
	const [selectedWorkflowIdsForBulkRevoke, setSelectedWorkflowIdsForBulkRevoke] = useState<
		string[]
	>([]);
	const [showShareAssociationsUploadModal, setShowShareAssociationsUploadModal] = useState(false);
	const [vestingDrawerOpened, setVestingDrawerOpened] = useState(false);
	const [vestingInvestorIds, setVestingInvestorIds] = useState<string[]>([]);
	const [transactionTypeDrawerOpened, setTransactionTypeDrawerOpened] = useState(false);
	const [transactionTypeSelection, setTransactionTypeSelection] =
		useState<InvestmentsBulkSelection | null>(null);
	const revokeMutation = useRevokeInvestmentWorkflowMutation(mipId);
	const releaseMutation = useReleaseInvestmentWorkflowMutation(mipId);
	const inviteMutation = useInviteInvestmentWorkflowMutation(mipId);
	const bulkReleaseMutation = useBulkReleaseMutation(mipId);
	const bulkRevokeMutation = useBulkRevokeMutation(mipId);
	const invalidateInvestmentsList = useInvalidateInvestmentsList(mipId);
	const prepareOneSchemaCacheRequest = useMutation({
		mutationFn: prepareOneSchemaShareAssociationsCache,
	});
	const upsertShareAssociationsRequest = useMutation({
		mutationFn: (records: OneSchemaShareAssociationRowValueDto[]) =>
			upsertShareAssociations(mipId, { records }),
		onSuccess: async () => {
			await invalidateInvestmentsList();
			setShowShareAssociationsUploadModal(false);
			showToastV2({
				title: 'Share associations upserted',
				state: 'success',
			});
		},
		onError: (error) => {
			if (isStandardBackendError(error)) {
				showToastV2({
					state: 'error',
					message: tDefault(`error_codes.${error.error.code}`),
				});
				return;
			}

			const message = getUserFacingErrorMessage(error);
			if (message) {
				showToastV2({
					state: 'error',
					message,
				});
			}
		},
	});

	const { value: investmentDetailsFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.INVESTMENT_DETAILS
	);

	const releaseMutationRef = useRef(releaseMutation);
	const revokeMutationRef = useRef(revokeMutation);
	// preventing re-render loops in the table
	releaseMutationRef.current = releaseMutation;
	revokeMutationRef.current = revokeMutation;

	const { hasPermission: hasExportViewerPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: dealId,
		permissionRole: PermissionRole.EXPORT_VIEWER,
	});

	const handleOpenKycDrawer = useCallback((kycWorkflowId: string) => {
		setSelectedKycWorkflowId(kycWorkflowId);
		setKycDrawerOpened(true);
	}, []);

	const handleOpenInvestorDataDrawer = useCallback((kycWorkflowId: string) => {
		setSelectedKycWorkflowId(kycWorkflowId);
		setInvestorDataDrawerOpened(true);
	}, []);

	const handleOpenTaxComplianceDrawer = useCallback((kycWorkflowId: string) => {
		setSelectedKycWorkflowId(kycWorkflowId);
		setTaxComplianceDrawerOpened(true);
	}, []);

	const handleOpenInviteModal = useCallback((investor: GetInvestmentsInvestorInfo) => {
		setInviteBulkPayload({ selectedIds: [investor.id] });
		setInviteModalOpened(true);
	}, []);

	const handleOpenRevokeDrawer = useCallback((investmentWorkflowId: string) => {
		setSelectedInvestmentWorkflowIdForRevoke(investmentWorkflowId);
		setRevokeDrawerOpened(true);
	}, []);

	const handleRelease = useCallback((investorId: string) => {
		releaseMutationRef.current.mutate(investorId);
	}, []);

	const handleRevoke = useCallback(() => {
		if (selectedInvestmentWorkflowIdForRevoke) {
			revokeMutationRef.current.mutate(selectedInvestmentWorkflowIdForRevoke);
		}
	}, [selectedInvestmentWorkflowIdForRevoke]);

	const handleSendInvitation = async (payload: SendInvestorInviteDto) => {
		await inviteMutation.mutateAsync(payload);
		setInviteModalOpened(false);
	};

	const { investors: investorsForModal, isLoading: isLoadingInvestorsForModal } =
		useInvestorsForInviteModal(inviteBulkPayload);

	const handleBulkInvite = useCallback((selectedRows: GetInvestmentsForMipItem[]) => {
		const selectedIds = selectedRows
			.filter((row) => !row.isCompleted)
			.map((row) => row.investor.id);
		setInviteBulkPayload({ selectedIds });
		setInviteModalOpened(true);
		setEligibilityFilter(null);
	}, []);

	const handleBulkRelease = (selectedRows: GetInvestmentsForMipItem[]) => {
		const investorIds = selectedRows
			.filter((row) => row.isEligibleForRelease)
			.map((row) => row.investor.id);
		if (investorIds.length > 0) {
			bulkReleaseMutation.mutate(investorIds);
		}
		setEligibilityFilter(null);
	};

	const handleBulkRevoke = useCallback((selectedRows: GetInvestmentsForMipItem[]) => {
		const workflowIds = selectedRows
			.filter((row) => !row.isCompleted)
			.map((row) => row.investmentWorkflowId);
		if (workflowIds.length > 0) {
			setSelectedWorkflowIdsForBulkRevoke(workflowIds);
			setBulkRevokeDrawerOpened(true);
		}
		setEligibilityFilter(null);
	}, []);

	const { vestingTerms, refetchVestingTerms } = useMIPManageContext();

	const handleBulkVestingTerms = useCallback(
		(selectedRows: GetInvestmentsForMipItem[]) => {
			const investorIds = selectedRows.map((row) => row.investor.id);
			setVestingInvestorIds(investorIds);
			void refetchVestingTerms();
			setVestingDrawerOpened(true);
		},
		[refetchVestingTerms]
	);

	const handleRowVestingTerms = useCallback(
		(investorId: string) => {
			setVestingInvestorIds([investorId]);
			void refetchVestingTerms();
			setVestingDrawerOpened(true);
		},
		[refetchVestingTerms]
	);

	const vestingSecuritiesQuery = useQuery({
		queryKey: ['vestingDrawerSecurities', mipId, vestingInvestorIds],
		queryFn: ({ signal }) =>
			getMIPInvestorSecuritiesList({ selectedIds: vestingInvestorIds }, signal),
		enabled: vestingDrawerOpened && vestingInvestorIds.length > 0,
	});

	const defaultVestingTermId = useMemo<string | null | undefined>(() => {
		if (vestingInvestorIds.length !== 1) {
			return undefined;
		}

		const securities = vestingSecuritiesQuery.data?.payload ?? [];
		const subjectToVesting = securities.filter((s) => s.isSubjectToVesting);

		if (subjectToVesting.length === 0) {
			return null;
		}

		const first = subjectToVesting[0]?.vestingTermId ?? null;
		const allAgree = subjectToVesting.every((s) => (s.vestingTermId ?? null) === first);

		return allAgree ? first : null;
	}, [vestingInvestorIds, vestingSecuritiesQuery.data?.payload]);

	const bulkVestingMutation = useMutation({
		mutationFn: bulkUpdateInvestorVestingTerms,
		onSuccess: () => {
			showToastV2({ title: tMipManage('investors.success'), state: 'success' });
			setVestingDrawerOpened(false);
		},
		onError: (error) => {
			showToastV2({
				title: tMipManage('investments.vesting.update_error', {
					defaultValue: 'Could not update vesting terms',
				}),
				message: isStandardBackendError(error)
					? tDefault(`error_codes.${error.error.code}`)
					: undefined,
				state: 'error',
			});
		},
	});

	const handleUpdateVestingTerms = (
		args: Omit<
			BulkUpdateInvestorsVestingDto,
			'selectedIds' | 'excludedIds' | 'filterQuery' | 'searchQuery'
		>
	) => {
		bulkVestingMutation.mutate({ selectedIds: vestingInvestorIds, ...args });
	};

	const handleConfirmBulkRevoke = useCallback(() => {
		if (selectedWorkflowIdsForBulkRevoke.length > 0) {
			bulkRevokeMutation.mutate(selectedWorkflowIdsForBulkRevoke);
		}
	}, [bulkRevokeMutation, selectedWorkflowIdsForBulkRevoke]);

	const {
		columns,
		investments,
		investmentsLoading,
		hasEditorPermission,
		columnVisibility,
		handleColumnVisibilityChange,
		mantineTableDataOperations,
		rowCount,
		disableSendInvestorInvitation,
		dataOperations,
	} = useInvestmentsTable({
		onKycClick: handleOpenKycDrawer,
		onInvestorDataClick: handleOpenInvestorDataDrawer,
		onTaxComplianceClick: handleOpenTaxComplianceDrawer,
		onInviteClick: handleOpenInviteModal,
		onRevokeClick: handleOpenRevokeDrawer,
		onReleaseClick: handleRelease,
		onVestingTermsClick: handleRowVestingTerms,
		eligibilityFilter,
	});
	const { downloadExcel, downloadOneSchemaAllocationsTemplate } = useInvestmentsBulkAction(
		dataOperations,
		eligibilityFilter
	);

	const handleBulkTransactionType = useCallback(
		(currentTable: Table<GetInvestmentsForMipItem>) => {
			const operationParams = getBulkOperationParams({
				operationType: getOperationType(currentTable),
				table: currentTable,
				dataOperations,
			});
			const selectedIds = operationParams.ids ?? [];
			const excludedIds = operationParams.excludedIds ?? [];

			setTransactionTypeSelection({
				selectedIds: selectedIds.length ? selectedIds : undefined,
				excludedIds: excludedIds.length ? excludedIds : undefined,
				// The armed eligibility filter is what narrowed the list to non-released,
				// so the filtered payload has to carry it too.
				filter: mapInvestmentsTableFilterToDto(
					dataOperations,
					EligibilityFilter.TRANSACTION_TYPE
				),
			});
			setTransactionTypeDrawerOpened(true);
		},
		[dataOperations]
	);

	const bulkTransactionTypeMutation = useMutation({
		mutationFn: (expectedTransactionType: ExpectedTransactionType | null) =>
			bulkUpdateExpectedTransactionType(mipId, {
				...transactionTypeSelection,
				expectedTransactionType,
			}),
		onSuccess: async (response) => {
			await invalidateInvestmentsList();
			setTransactionTypeDrawerOpened(false);
			setTransactionTypeSelection(null);
			setEligibilityFilter(null);
			showToastV2({
				title: tMipManage('investments.transaction_type.update_success', {
					count: response.payload?.updatedCount ?? 0,
				}),
				state: 'success',
			});
		},
		onError: (error) => {
			showToastV2({
				title: tMipManage('investments.transaction_type.update_error'),
				message: isStandardBackendError(error)
					? tDefault(`error_codes.${error.error.code}`)
					: undefined,
				state: 'error',
			});
		},
	});

	const isTableActionBusy =
		investmentsLoading ||
		prepareOneSchemaCacheRequest.isPending ||
		upsertShareAssociationsRequest.isPending;

	const handleOpenShareAssociationsUploadModal = useCallback(async () => {
		if (!oneSchemaUserConfig?.oneSchemaJwt) {
			return;
		}

		try {
			await prepareOneSchemaCacheRequest.mutateAsync({
				mipId,
				userJwt: oneSchemaUserConfig.oneSchemaJwt,
			});
			setShowShareAssociationsUploadModal(true);
		} catch {
			showToastV2({
				state: 'error',
				message: tMipManage('investments.toasts.errors.failed_prepare_allocation_upload'),
			});
		}
	}, [mipId, oneSchemaUserConfig?.oneSchemaJwt, prepareOneSchemaCacheRequest, tMipManage]);

	const handleUpsertShareAssociations = useCallback(
		({
			records,
			error_records,
		}: {
			records: OneSchemaShareAssociationRowValueDto[];
			error_records?: OneSchemaErrorRecord<OneSchemaShareAssociationRowValueDto>[];
		}) => {
			const warningOnlyRecords = (error_records ?? [])
				.filter((entry) => {
					const hasNonWarningErrors = Object.values(entry.errors)
						.flat()
						.some((code) => code !== 3002);
					return !hasNonWarningErrors;
				})
				.map((entry) => entry.data);

			upsertShareAssociationsRequest.mutate([...records, ...warningOnlyRecords]);
		},
		[upsertShareAssociationsRequest]
	);

	const quickFilterOptions: QuickFilterOption[] = useMemo(
		() => [
			{
				value: 'in_progress',
				label: tMipManage('investments.quick_filters.in_progress'),
				filterKey: 'isReleased',
				filterValue: false,
			},
			{
				value: 'released',
				label: tMipManage('investments.quick_filters.released'),
				filterKey: 'isReleased',
				filterValue: true,
			},
			{
				value: 'all',
				label: tMipManage('investments.quick_filters.all_investments'),
			},
		],
		[tMipManage]
	);

	return (
		<>
			<ProgressBar refreshInvestors={invalidateInvestmentsList} />
			<MantineTable
				getRowId={(row) => row.investmentWorkflowId}
				renderBulkActionsMenu={true}
				onActionCanceled={() => setEligibilityFilter(null)}
				actions={(table) =>
					createBulkActions({
						mipId,
						table,
						isLoading: isTableActionBusy,
						dataOperations,
						downloadExcel,
						downloadOneSchemaAllocationsTemplate,
						onOpenUpdateAllocationsImporter: handleOpenShareAssociationsUploadModal,
						onBulkInvite: handleBulkInvite,
						onBulkRelease: handleBulkRelease,
						onBulkRevoke: handleBulkRevoke,
						onBulkVestingTerms: handleBulkVestingTerms,
						onBulkTransactionType: handleBulkTransactionType,
						onBulkTransactionTypeSelect: () =>
							setEligibilityFilter(EligibilityFilter.TRANSACTION_TYPE),
						onBulkInviteSelect: () => setEligibilityFilter(EligibilityFilter.INVITE),
						onBulkReleaseSelect: () => setEligibilityFilter(EligibilityFilter.RELEASE),
						onBulkRevokeSelect: () => setEligibilityFilter(EligibilityFilter.REVOKE),
						onUpdateAllocationsSelect: () =>
							setEligibilityFilter(EligibilityFilter.UPDATE_ALLOCATIONS),
						disableSendInvestorInvitation: disableSendInvestorInvitation ?? false,
						hasEditorPermission,
						showUpdateAllocations:
							hasEditorPermission &&
							!!oneSchemaUserConfig?.oneSchemaClientId &&
							!!oneSchemaUserConfig?.oneSchemaJwt,
						showDownloadExcel: hasExportViewerPermission,
						tMipManage,
					})
				}
				enablePagination={true}
				enableColumnResizing={true}
				pinnedColumnIds={['actions', 'investor']}
				manualPagination={true}
				enableSearchBar={true}
				mantineTableBodyRowProps={({ row }) => ({
					id: row.id,
					style: {
						cursor: 'pointer',
						height: 'unset',
						placeItems: row.getIsExpanded() ? 'flex-start' : 'center',
					},
					onClick: () => {
						if (investmentDetailsFeatureFlag) {
							navigate({
								pathname: mipManagePath(MIP_MANAGE_ROUTES.INVESTMENT_DETAILS, {
									dealId,
									mipId,
									investmentId: row.id,
								}),
								search,
							});
						} else {
							row.toggleExpanded();
						}
					},
				})}
				enableExpanding={true}
				columns={columns}
				isLoading={investmentsLoading}
				data={investments}
				enableRowActions={false}
				enableStickyHeader={true}
				initialState={{
					columnVisibility,
				}}
				state={{
					columnVisibility,
				}}
				onColumnVisibilityChange={handleColumnVisibilityChange}
				dataOperations={mantineTableDataOperations}
				rowCount={rowCount}
				quickFilterOptions={quickFilterOptions}
			/>
			<InvestmentWorkflowKycSettingsDrawer
				mipId={mipId}
				opened={kycDrawerOpened}
				onClose={() => setKycDrawerOpened(false)}
				kycWorkflowId={selectedKycWorkflowId}
			/>
			<InvestmentWorkflowInvestorDataDrawer
				opened={investorDataDrawerOpened}
				onClose={() => setInvestorDataDrawerOpened(false)}
				kycWorkflowId={selectedKycWorkflowId}
			/>
			<TaxAndComplianceDrawer
				mipId={mipId}
				opened={taxComplianceDrawerOpened}
				onClose={() => {
					setTaxComplianceDrawerOpened(false);
					void invalidateInvestmentsList();
				}}
				kycWorkflowId={selectedKycWorkflowId}
			/>

			{inviteModalOpened && (
				<SendInvitationModal
					key={investorsForModal.length}
					onClose={() => {
						setInviteModalOpened(false);
						setInviteBulkPayload({ selectedIds: [] });
					}}
					sendInvitation={handleSendInvitation}
					isLoading={inviteMutation.isPending || isLoadingInvestorsForModal}
					investors={investorsForModal}
				/>
			)}

			<ConfirmDeleteDrawer
				opened={revokeDrawerOpened}
				onClose={() => {
					setRevokeDrawerOpened(false);
					setSelectedInvestmentWorkflowIdForRevoke(undefined);
				}}
				title={tMipManage('investors.expanded_row.revoke_drawer.title')}
				promptText={tMipManage('investors.expanded_row.revoke_drawer.promptText')}
				confirmDeleteText={tMipManage(
					'investors.expanded_row.revoke_drawer.confirmDeleteText'
				)}
				inputLabel={tMipManage('investors.expanded_row.revoke_drawer.inputLabel')}
				inputPlaceholder={tMipManage(
					'investors.expanded_row.revoke_drawer.inputPlaceholder'
				)}
				wordMismatchErrorMessage={tMipManage(
					'investors.expanded_row.revoke_drawer.wordMismatchErrorMessage'
				)}
				deleteBtnText={tMipManage('investors.expanded_row.revoke_drawer.deleteBtnText')}
				cancelBtnText={tMipManage('investors.expanded_row.revoke_drawer.cancelBtnText')}
				handleDelete={handleRevoke}
				deleteSuccess={revokeMutation.isSuccess}
				deleteLoading={revokeMutation.isPending}
				enableDeleteButtonOnlyOnConfirmDelete
			/>
			<ConfirmDeleteDrawer
				opened={bulkRevokeDrawerOpened}
				onClose={() => {
					setBulkRevokeDrawerOpened(false);
					setSelectedWorkflowIdsForBulkRevoke([]);
				}}
				title={tMipManage('investors.bulk_revoke_drawer.title')}
				promptText={tMipManage('investors.bulk_revoke_drawer.promptText')}
				confirmDeleteText={tMipManage('investors.bulk_revoke_drawer.confirmDeleteText')}
				inputLabel={tMipManage('investors.bulk_revoke_drawer.inputLabel')}
				inputPlaceholder={tMipManage('investors.bulk_revoke_drawer.inputPlaceholder')}
				wordMismatchErrorMessage={tMipManage(
					'investors.bulk_revoke_drawer.wordMismatchErrorMessage'
				)}
				deleteBtnText={tMipManage('investors.bulk_revoke_drawer.deleteBtnText')}
				cancelBtnText={tMipManage('investors.bulk_revoke_drawer.cancelBtnText')}
				handleDelete={handleConfirmBulkRevoke}
				deleteSuccess={bulkRevokeMutation.isSuccess}
				deleteLoading={bulkRevokeMutation.isPending}
				enableDeleteButtonOnlyOnConfirmDelete
			/>

			<VestingTermsBulkDrawer
				opened={vestingDrawerOpened}
				onClose={() => setVestingDrawerOpened(false)}
				vestingTerms={vestingTerms}
				securitiesList={vestingSecuritiesQuery.data?.payload ?? []}
				updateVestingTerms={handleUpdateVestingTerms}
				loading={bulkVestingMutation.isPending || vestingSecuritiesQuery.isFetching}
				defaultVestingTermId={defaultVestingTermId}
			/>

			<TransactionTypeBulkDrawer
				opened={transactionTypeDrawerOpened}
				onClose={() => {
					setTransactionTypeDrawerOpened(false);
					setEligibilityFilter(null);
				}}
				updateTransactionType={(expectedTransactionType) =>
					bulkTransactionTypeMutation.mutate(expectedTransactionType)
				}
				loading={bulkTransactionTypeMutation.isPending}
			/>

			{!!oneSchemaUserConfig?.oneSchemaClientId && showShareAssociationsUploadModal && (
				<OneSchemaImporterModal
					isOpen={showShareAssociationsUploadModal}
					onRequestClose={() => setShowShareAssociationsUploadModal(false)}
					onSuccess={handleUpsertShareAssociations}
					onError={(message) =>
						showToastV2({
							state: 'error',
							message,
						})
					}
					clientId={oneSchemaUserConfig.oneSchemaClientId}
					userJwt={oneSchemaUserConfig.oneSchemaJwt}
					templateKey={OneSchemaTemplateKey.UPLOAD_SHARE_ASSOCIATIONS}
					devMode={process.env.NODE_ENV !== 'production'}
					importConfig={{ type: 'local' }}
				/>
			)}
		</>
	);
};
